import {
  VegaBox,
  VegaButton,
  VegaChip,
  VegaFlex,
  VegaFont,
  VegaPageNotification,
  VegaSectionTitle,
} from '@globalpayments/vega-react';
import { useEffect, useRef, useState } from 'react';
import useQueueStatusData from '../../hooks/useQueueStatusData';
import { QueueModel, QueueStatusCheckModel, QueueActionParam } from '../../types/queueStatus';
import { groupBy } from 'lodash';

import statusApiFunctions from '../../api/status';

import styles from './index.module.css';
import QueueStatusApiGroup from './components/QueueStatusApiGroup';
import PageSkeleton from '../pageSkeleton';

const QueueStatuses = () => {
  const [lastRefreshTime, setLastRefreshTime] = useState<string>();
  const [queueData, setQueueData] = useState<Array<QueueStatusCheckModel>>();
  const [resStatus, setResStatus] = useState<'success' | 'error'>('success');
  const [showNotification, setShowNotification] = useState(false);
  const [pendingActionKey, setPendingActionKey] = useState<string>();
  const notificationTimer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(notificationTimer.current), []);

  const handleAction = async (queueModel: QueueModel, actionName: string) => {
    const actionKey = `${queueModel.apiName}-${queueModel.queueName}-${queueModel.subQueueName}-${actionName}`;
    const actionParam: QueueActionParam = {
      queueModel: queueModel,
      queueActionName: actionName,
    };
    setPendingActionKey(actionKey);
    try {
      const res = await statusApiFunctions.performQueueAction(actionParam);
      setResStatus(res === '' ? 'success' : 'error');
    } catch {
      setResStatus('error');
    } finally {
      setShowNotification(true);
      window.clearTimeout(notificationTimer.current);
      notificationTimer.current = window.setTimeout(() => setShowNotification(false), 3000);
      await refetch();
      setPendingActionKey(undefined);
    }
  };

  const handleSuccess = (data: unknown) => {
    if (Array.isArray(data)) {
      setQueueData(data);
      setLastRefreshTime(new Date().toLocaleString());
    } else if (data !== undefined) {
      setQueueData(undefined);
    }
  };

  const { data, isFetching, isLoading, isError, refetch } = useQueueStatusData(handleSuccess);
  const displayedQueueData = queueData ?? data;
  const groupedQueues = groupBy(displayedQueueData ?? [], (status) => status.queueModel.apiName);
  const apiNames = Object.keys(groupedQueues);
  const queueCount = apiNames.reduce(
    (total, apiName) =>
      total + Object.keys(groupBy(groupedQueues[apiName], (status) => status.queueModel.queueName)).length,
    0,
  );
  const totalMessages = displayedQueueData?.reduce((total, status) => total + status.count, 0) ?? 0;
  const actionableQueues =
    displayedQueueData?.filter((status) => status.availableQueueActionNames.length > 0).length ?? 0;
  const loadErrorMessage = 'An error has occurred while trying to load the available Queue Statuses';

  return (
    <VegaBox className={styles.queueStatusCheckContainer}>
      <VegaFlex direction="col" gap="size-32">
        <VegaFlex alignItems="center" flexWrap="wrap" gap="size-16" justifyContent="space-between">
          <VegaSectionTitle titleText="Queue Status Checks" />
          {!isLoading && !isError && displayedQueueData && (
            <VegaFlex alignItems="center" flexWrap="wrap" gap="size-16">
              <VegaFont as="span" color="text-secondary" variant="font-p2-short">
                Last refreshed: {lastRefreshTime}
              </VegaFont>
              <VegaButton
                aria-label="RefreshQueueCheckButton"
                data-testid="refresh-button"
                icon="fa-solid fa-rotate"
                label="Refresh All"
                loading={isFetching}
                onVegaClick={() => refetch()}
                size="small"
                variant="secondary"
              />
            </VegaFlex>
          )}
        </VegaFlex>

        {!isLoading && !isError && displayedQueueData && (
          <VegaFlex flexWrap="wrap" gap="size-8">
            <VegaChip chipType="label" size="small" text={`APIs ${apiNames.length}`} variant="outlined" />
            <VegaChip chipType="label" size="small" text={`Queues ${queueCount}`} variant="outlined" />
            <VegaChip
              chipType="status"
              size="small"
              statusColor={totalMessages > 0 ? 'bg-status-warning' : 'bg-status-success'}
              text={`Messages ${totalMessages}`}
            />
            <VegaChip
              chipType="status"
              size="small"
              statusColor="bg-status-info"
              text={`Actionable ${actionableQueues}`}
            />
          </VegaFlex>
        )}

        {isLoading ? (
          <PageSkeleton label="Loading queue statuses" variant="table" />
        ) : isError || !displayedQueueData ? (
          <VegaPageNotification
            duration={0}
            message={loadErrorMessage}
            notificationTitle="Queue status checks unavailable"
            type="error"
          />
        ) : apiNames.length > 0 ? (
          apiNames.map((apiName) => (
            <QueueStatusApiGroup
              apiName={apiName}
              apiQueues={groupedQueues[apiName]}
              key={apiName}
              pendingActionKey={pendingActionKey}
              onAction={handleAction}
            />
          ))
        ) : (
          <VegaPageNotification
            duration={0}
            message="No queue status checks are configured for this environment."
            notificationTitle="No queue checks"
            type="info"
          />
        )}
      </VegaFlex>
      {showNotification && (
        <VegaPageNotification
          duration={3000}
          message={
            resStatus === 'success' ? 'Queue Action Succeeded' : 'There was a problem executing the Queue Action'
          }
          notificationTitle="Queue action result"
          showCloseButton
          type={resStatus}
        />
      )}
    </VegaBox>
  );
};

export default QueueStatuses;
